import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../services/api';

const BillingDashboard = () => {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('balance');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceRes, transactionsRes, settingsRes] = await Promise.all([
        api.get('/billing/balance'),
        api.get('/billing/transactions?Limit=20'),
        api.get('/billing/notification-settings')
      ]);
      
      setBalance(balanceRes.data);
      setTransactions(transactionsRes.data.Transactions);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      const res = await api.put('/billing/notification-settings', updates);
      setSettings(res.data);
      toast.success('Настройки уведомлений обновлены');
    } catch (error) {
      toast.error('Ошибка обновления настроек: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <h1 className="text-3xl font-bold mb-6">Управление балансом</h1>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('balance')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'balance'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Баланс
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'transactions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            История операций
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Уведомления
          </button>
        </nav>
      </div>

      {/* Balance Tab */}
      {activeTab === 'balance' && balance && (
        <BalanceCard balance={balance} />
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <TransactionsTable transactions={transactions} />
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && settings && (
        <NotificationSettings settings={settings} onUpdate={updateSettings} />
      )}
    </div>
  );
};

// Balance Card Component
const BalanceCard = ({ balance }) => {
  const getStatusColor = () => {
    const bal = parseFloat(balance.Balance);
    const critical = parseFloat(balance.Critical_Threshold);
    const warning = parseFloat(balance.Warning_Threshold);
    
    if (bal <= critical) return 'bg-red-500';
    if (bal <= warning) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    const bal = parseFloat(balance.Balance);
    const critical = parseFloat(balance.Critical_Threshold);
    const warning = parseFloat(balance.Warning_Threshold);
    
    if (bal <= critical) return 'Критический уровень';
    if (bal <= warning) return 'Низкий баланс';
    return 'Достаточно средств';
  };

  const progressPercent = Math.min(
    (parseFloat(balance.Balance) / parseFloat(balance.Warning_Threshold)) * 100,
    100
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Текущий баланс</h2>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${getStatusColor()}`}></div>
          <span className="text-sm text-gray-600">{getStatusText()}</span>
        </div>
      </div>

      <div className="mb-4">
        <div className="text-4xl font-bold text-gray-900">
          {parseFloat(balance.Balance).toFixed(2)} {balance.Currency}
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Потрачено всего: {parseFloat(balance.Total_Spent).toFixed(2)} {balance.Currency}
        </p>
        <p className="text-sm text-gray-500">
          Пополнено всего: {parseFloat(balance.Total_Deposited).toFixed(2)} {balance.Currency}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Критично: {balance.Critical_Threshold}</span>
          <span>Предупреждение: {balance.Warning_Threshold}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${getStatusColor()}`}
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
      </div>

      {/* Stats */}
      {balance.Stats && (
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
          <div>
            <p className="text-xs text-gray-500">Расходы за неделю</p>
            <p className="text-lg font-semibold">{parseFloat(balance.Stats.Week_Spending).toFixed(2)} {balance.Currency}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Средний расход/день</p>
            <p className="text-lg font-semibold">{parseFloat(balance.Stats.Avg_Daily_Cost).toFixed(2)} {balance.Currency}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Дней осталось</p>
            <p className="text-lg font-semibold">
              {balance.Stats.Days_Remaining !== null ? `~${balance.Stats.Days_Remaining}` : '∞'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Transactions Table Component
const TransactionsTable = ({ transactions }) => {
  const getTypeColor = (type) => {
    switch (type) {
      case 'DEPOSIT':
        return 'bg-green-100 text-green-800';
      case 'WITHDRAWAL':
        return 'bg-red-100 text-red-800';
      case 'REFUND':
        return 'bg-blue-100 text-blue-800';
      case 'ADJUSTMENT':
        return 'bg-yellow-100 text-yellow-800';
      case 'BONUS':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      DEPOSIT: 'Пополнение',
      WITHDRAWAL: 'Списание',
      REFUND: 'Возврат',
      ADJUSTMENT: 'Корректировка',
      BONUS: 'Бонус'
    };
    return labels[type] || type;
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold">История операций</h2>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Дата
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Тип
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Сумма
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Баланс после
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Описание
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transactions.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  Нет операций
                </td>
              </tr>
            ) : (
              transactions.map((tx) => (
                <tr key={tx.ID}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(tx.Created_At).toLocaleString('ru-RU')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(tx.Type)}`}>
                      {getTypeLabel(tx.Type)}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    tx.Type === 'WITHDRAWAL' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {tx.Type === 'WITHDRAWAL' ? '-' : '+'}
                    {parseFloat(tx.Amount).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {parseFloat(tx.Balance_After).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {tx.Description || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Notification Settings Component
const NotificationSettings = ({ settings, onUpdate }) => {
  const [formData, setFormData] = useState(settings);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-4">Настройки уведомлений</h2>

      <form onSubmit={handleSubmit}>
        {/* Email Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Email уведомления</h3>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Email_Enabled}
                onChange={(e) => handleChange('Email_Enabled', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Включить email уведомления</span>
            </label>
            {formData.Email_Enabled && (
              <input
                type="email"
                value={formData.Email_Address || ''}
                onChange={(e) => handleChange('Email_Address', e.target.value)}
                placeholder="email@example.com"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            )}
          </div>
        </div>

        {/* Telegram Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Telegram уведомления</h3>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Telegram_Enabled}
                onChange={(e) => handleChange('Telegram_Enabled', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Включить Telegram уведомления</span>
            </label>
            {formData.Telegram_Enabled && (
              <input
                type="text"
                value={formData.Telegram_Chat_ID || ''}
                onChange={(e) => handleChange('Telegram_Chat_ID', e.target.value)}
                placeholder="@username или chat_id"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            )}
          </div>
        </div>

        {/* Webhook Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Webhook уведомления</h3>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Webhook_Enabled}
                onChange={(e) => handleChange('Webhook_Enabled', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Включить Webhook уведомления</span>
            </label>
            {formData.Webhook_Enabled && (
              <input
                type="url"
                value={formData.Webhook_URL || ''}
                onChange={(e) => handleChange('Webhook_URL', e.target.value)}
                placeholder="https://your-server.com/webhook"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            )}
          </div>
        </div>

        {/* Event Types */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Типы событий</h3>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Low_Balance_Alert}
                onChange={(e) => handleChange('Low_Balance_Alert', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Низкий баланс</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Critical_Balance}
                onChange={(e) => handleChange('Critical_Balance', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Критический баланс</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Zero_Balance}
                onChange={(e) => handleChange('Zero_Balance', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Недостаточно средств</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Deposit_Confirmation}
                onChange={(e) => handleChange('Deposit_Confirmation', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Подтверждение пополнения</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Daily_Report}
                onChange={(e) => handleChange('Daily_Report', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Ежедневный отчет</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.Weekly_Report}
                onChange={(e) => handleChange('Weekly_Report', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2">Еженедельный отчет</span>
            </label>
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Сохранить настройки
        </button>
      </form>
    </div>
  );
};

export default BillingDashboard;
